import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';
import { api } from '../services/api';
import { socketService } from '../utils/socketService';
import AdminBroadcastModal, { isAdminBroadcastNotification } from '../components/common/AdminBroadcastModal';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [adminBroadcast, setAdminBroadcast] = useState(null);
  const adminBroadcastPopupIdsRef = useRef(new Set());
  const { user } = useAuth();

  const isNotificationRead = useCallback((n) => {
    return Boolean(n?.is_read || n?.isRead || n?.read);
  }, []);

  const normalizeNotification = useCallback((n) => {
    if (!n) return null;
    return {
      ...n,
      is_read: Boolean(n.is_read || n.isRead || n.read),
      created_at: n.created_at || n.createdAt || new Date().toISOString(),
      data: n.data || n.payload || {},
    };
  }, []);

  const tryOpenAdminBroadcastModal = useCallback(
    (raw) => {
      if (!raw) return;
      const normalized = normalizeNotification(raw);
      if (!normalized || !isAdminBroadcastNotification(normalized)) return;
      const recordId = normalized.id ?? normalized.data?.notificationId;
      if (recordId == null || recordId === '') return;
      const idKey = String(recordId);
      if (adminBroadcastPopupIdsRef.current.has(idKey)) return;
      adminBroadcastPopupIdsRef.current.add(idKey);
      setAdminBroadcast({
        title: normalized.title || 'Annonce',
        message: normalized.message || '',
        type: normalized.type || 'admin_info',
        recordId,
      });
    },
    [normalizeNotification]
  );

  // Charger les notifications depuis l'API avec useCallback pour éviter les re-rendus inutiles
  const loadNotifications = useCallback(async () => {
    if (!user) return [];
    
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await api.get('/notifications', {
        params: {
          limit: 50,
          sort: '-created_at'
        }
      });
      
      // Handle the response based on its structure
      const responseData = response.data || response;
      
      if (Array.isArray(responseData)) {
        const normalized = responseData.map(normalizeNotification).filter(Boolean);
        setNotifications(normalized);
        const unread = normalized.filter(n => !isNotificationRead(n)).length;
        setUnreadCount(unread);
        return normalized;
      } else if (responseData && Array.isArray(responseData.notifications)) {
        const normalized = responseData.notifications.map(normalizeNotification).filter(Boolean);
        setNotifications(normalized);
        const unread = normalized.filter(n => !isNotificationRead(n)).length;
        setUnreadCount(unread);
        return normalized;
      }
      return response.data || [];
    } catch (error) {
      console.error('Erreur lors du chargement des notifications:', error);
      setError('Impossible de charger les notifications');
      toast.error('Impossible de charger les notifications');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [user, normalizeNotification, isNotificationRead]);

  // Marquer une notification comme lue
  const markAsRead = async (notificationId) => {
    if (!notificationId) return { success: false, error: 'ID de notification manquant' };
    
    // Sauvegarder l'état précédent pour annulation en cas d'erreur
    const previousNotifications = [...notifications];
    const previousUnreadCount = unreadCount;
    
    try {
      // Mise à jour optimiste
      const updatedNotifications = notifications.map(notif => 
        notif.id === notificationId ? { ...notif, read: true, is_read: true, isRead: true } : notif
      );
      
      setNotifications(updatedNotifications);
      
      // Mettre à jour le compteur de notifications non lues
      const newUnreadCount = Math.max(0, previousUnreadCount - 1);
      setUnreadCount(newUnreadCount);
      
      // Appel API pour marquer comme lue
      const response = await api.put(`/notifications/${notificationId}/read`);
      
      // Vérifier la réponse de l'API
      if (!response || (response.status && response.status >= 400)) {
        // Annuler la mise à jour optimiste en cas d'erreur
        setNotifications(previousNotifications);
        setUnreadCount(previousUnreadCount);
        throw new Error('Échec du marquage comme lu');
      }
      
      return { success: true };
    } catch (error) {
      console.error('Erreur lors du marquage comme lu:', error);
      // Annuler la mise à jour optimiste en cas d'erreur
      setNotifications(previousNotifications);
      setUnreadCount(previousUnreadCount);
      toast.error(error.message || 'Erreur lors du marquage de la notification comme lue');
      return { success: false, error: error.message };
    }
  };

  // Marquer toutes les notifications comme lues
  const markAllAsRead = async () => {
    const unreadNotifications = notifications.filter(n => !n.read);
    
    if (unreadNotifications.length === 0) {
      return { success: true };
    }
    
    // Sauvegarder l'état précédent pour annulation en cas d'erreur
    const previousNotifications = [...notifications];
    const previousUnreadCount = unreadCount;
    
    try {
      // Mise à jour optimiste
      const updatedNotifications = notifications.map(n => ({ ...n, read: true }));
      setNotifications(updatedNotifications);
      setUnreadCount(0);
      
      // Appel API
      const response = await api.put('/notifications/mark-all/read');
      
      // Vérifier la réponse de l'API
      if (!response || (response.status && response.status >= 400)) {
        // Annuler la mise à jour optimiste en cas d'erreur
        setNotifications(previousNotifications);
        setUnreadCount(previousUnreadCount);
        throw new Error('Échec du marquage des notifications comme lues');
      }
      
      return { success: true };
    } catch (error) {
      console.error('Erreur lors du marquage comme lues:', error);
      // Annuler la mise à jour optimiste en cas d'erreur
      setNotifications(previousNotifications);
      setUnreadCount(previousUnreadCount);
      toast.error(error.message || 'Impossible de marquer toutes les notifications comme lues');
      return { success: false, error: error.message };
    }
  };

  // Gérer le clic sur une notification
  const handleNotificationClick = (notification) => {
    // Marquer la notification comme lue
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    // Rediriger vers la page appropriée en fonction du type de notification
    if (notification.data && notification.data.redirectTo) {
      // Implémentez la logique de redirection ici
      // Par exemple: history.push(notification.data.redirectTo);
      console.log('Redirection vers:', notification.data.redirectTo);
    }
  };

  // Chargement initial uniquement : les mises à jour viennent de Socket.IO (effet ci‑dessous).
  useEffect(() => {
    if (!user) return;

    let isMounted = true;
    loadNotifications().then(() => {
      if (!isMounted) return;
    });

    return () => {
      isMounted = false;
    };
  }, [user, loadNotifications]);

  // Annonces admin non lues après chargement (ex. utilisateur hors ligne au moment du broadcast)
  useEffect(() => {
    if (!user || isLoading) return;
    const candidate = notifications.find((n) => isAdminBroadcastNotification(n) && !isNotificationRead(n));
    if (candidate) tryOpenAdminBroadcastModal(candidate);
  }, [user, isLoading, notifications, isNotificationRead, tryOpenAdminBroadcastModal]);

  // Écoute temps réel des notifications via Socket.IO
  useEffect(() => {
    if (!user) return undefined;
    const token = localStorage.getItem('token');
    if (!token) return undefined;

    socketService.connect().catch(() => {});

    const onConnect = () => setIsConnected(true);
    const onDisconnect = () => setIsConnected(false);
    const onError = () => setIsConnected(false);
    const broadcastRealtime = (event, payload) => {
      try {
        window.dispatchEvent(
          new CustomEvent('liberty:realtime', {
            detail: { event, payload },
          })
        );
      } catch (_) {}
    };

    const upsertRealtimeNotification = (rawPayload, fallbackType = 'info') => {
      if (!rawPayload) return;
      const payload = {
        ...rawPayload,
        type: rawPayload.type || fallbackType,
      };
      const normalized = normalizeNotification(payload);
      if (!normalized) return;
      const stableId =
        normalized.id ||
        normalized.notificationId ||
        normalized.data?.notificationId ||
        `${normalized.type || fallbackType}_${normalized.data?.missionId || normalized.missionId || Date.now()}`;
      const normalizedWithId = {
        ...normalized,
        id: stableId,
      };
      setNotifications((prev) => {
        const exists = prev.some((n) => String(n.id) === String(normalizedWithId.id));
        const next = exists
          ? prev.map((n) => (String(n.id) === String(normalizedWithId.id) ? { ...n, ...normalizedWithId } : n))
          : [normalizedWithId, ...prev];
        setUnreadCount(next.filter((n) => !isNotificationRead(n)).length);
        return next;
      });
    };

    const onNotification = (payload) => {
      upsertRealtimeNotification(payload, payload?.type || 'notification');
      tryOpenAdminBroadcastModal(payload);
      broadcastRealtime('notification', payload);
    };
    const onPriceProposed = (payload) => {
      const proposedPrice = payload?.proposedPrice;
      const demenageurName = payload?.demenageurName || 'Déménageur';
      upsertRealtimeNotification(
        {
          id: payload?.notificationId || null,
          type: 'price_proposed',
          title: 'Nouvelle proposition de prix',
          message:
            proposedPrice != null
              ? `${demenageurName} propose ${proposedPrice} TND pour votre mission.`
              : `${demenageurName} a envoyé une proposition de prix.`,
          data: payload || {},
          createdAt: new Date().toISOString(),
        },
        'price_proposed'
      );
      toast.info('Nouvelle proposition de prix reçue');
      broadcastRealtime('price_proposed', payload);
    };

    const onNegotiationAccepted = (payload) => {
      upsertRealtimeNotification(
        {
          id: payload?.notificationId || null,
          type: 'negotiation_accepted',
          title: 'Négociation acceptée',
          message: payload?.message || 'Votre négociation a été acceptée.',
          data: payload || {},
          createdAt: new Date().toISOString(),
        },
        'negotiation_accepted'
      );
      broadcastRealtime('negotiation_accepted', payload);
    };

    const onStatusUpdated = (payload) => {
      upsertRealtimeNotification(
        {
          id: payload?.notificationId || null,
          type: 'status_updated',
          title: 'Statut mission mis à jour',
          message: payload?.message || 'Le statut de votre mission a changé.',
          data: payload || {},
          createdAt: new Date().toISOString(),
        },
        'status_updated'
      );
      broadcastRealtime('status_updated', payload);
    };

    const onClientPriceProposed = (payload) => {
      upsertRealtimeNotification(
        {
          id: payload?.notificationId || null,
          type: 'client_price_proposed',
          title: 'Nouvelle contre-proposition client',
          message:
            payload?.proposedPrice != null
              ? `Le client propose ${payload.proposedPrice} TND.`
              : 'Le client a envoyé une contre-proposition.',
          data: payload || {},
          createdAt: new Date().toISOString(),
        },
        'client_price_proposed'
      );
      broadcastRealtime('client_price_proposed', payload);
    };

    const onPriceAccepted = (payload) => {
      upsertRealtimeNotification(
        {
          id: payload?.notificationId || null,
          type: 'price_accepted',
          title: 'Prix accepté',
          message: payload?.message || 'Le prix a été accepté.',
          data: payload || {},
          createdAt: new Date().toISOString(),
        },
        'price_accepted'
      );
      broadcastRealtime('price_accepted', payload);
    };

    const onServiceRequestCreated = (payload) => {
      broadcastRealtime('service_request_created', payload);
    };

    const onNewServiceRequest = (payload) => {
      broadcastRealtime('new_service_request', payload);
    };

    const onChatCreated = (payload) => broadcastRealtime('chat_created', payload);
    const onChatDeleted = (payload) => broadcastRealtime('chat_deleted', payload);
    const onNewMessage = (payload) => broadcastRealtime('new_message', payload);
    const onMissionPurged = (payload) => broadcastRealtime('mission_purged', payload);
    const onQuickServicePeerClaimed = (payload) => broadcastRealtime('quick_service_peer_claimed', payload);
    const onPresence = (payload) => broadcastRealtime('demenageur:presence', payload);

    const disposers = [
      socketService.on('connect', onConnect),
      socketService.on('disconnect', onDisconnect),
      socketService.on('connect_error', onError),
      socketService.on('notification', onNotification),
      socketService.on('price_proposed', onPriceProposed),
      socketService.on('client_price_proposed', onClientPriceProposed),
      socketService.on('price_accepted', onPriceAccepted),
      socketService.on('negotiation_accepted', onNegotiationAccepted),
      socketService.on('status_updated', onStatusUpdated),
      socketService.on('service_request_created', onServiceRequestCreated),
      socketService.on('new_service_request', onNewServiceRequest),
      socketService.on('chat_created', onChatCreated),
      socketService.on('chat_deleted', onChatDeleted),
      socketService.on('new_message', onNewMessage),
      socketService.on('mission_purged', onMissionPurged),
      socketService.on('quick_service_peer_claimed', onQuickServicePeerClaimed),
      socketService.on('demenageur:presence', onPresence),
    ];

    return () => {
      disposers.forEach((dispose) => {
        if (typeof dispose === 'function') dispose();
      });
    };
  }, [user, normalizeNotification, isNotificationRead, tryOpenAdminBroadcastModal]);

  const handleAdminBroadcastConfirm = () => {
    const id = adminBroadcast?.recordId;
    if (id != null && id !== '') {
      markAsRead(id).catch(() => {});
    }
    setAdminBroadcast(null);
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
unreadCount,
        isLoading,
        error,
        isConnected,
        loadNotifications,
        refreshNotifications: loadNotifications,
        markAsRead,
        markAllAsRead,
      }}
    >
      {children}
      <AdminBroadcastModal
        open={Boolean(adminBroadcast)}
        title={adminBroadcast?.title}
        message={adminBroadcast?.message}
        type={adminBroadcast?.type}
        onConfirm={handleAdminBroadcastConfirm}
      />
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications doit être utilisé à l\'intérieur de NotificationProvider');
  }
  return context;
};
